import React, { useEffect, useState } from "react";
import axios from "axios";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography
} from "@mui/material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const SoilTestTable = () => {
  const [data, setData] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [userId, setUserId] = useState("");
  const [status, setStatus] = useState("complete");
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [token, setToken] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (userId) params.user_id = userId;
      if (status) params.status = status;

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v1/soil_test`,
        { params }
      );

      const formattedData = response.data.map(item => ({ ...item, id: item.id }));
      setData(formattedData);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilter = () => fetchData();

  const handleReset = () => {
    setFromDate("");
    setToDate("");
    setUserId("");
    setStatus("");
    fetchData();
  };

  const handleViewDetails = (row) => {
    setSelectedRow(row);
    setOpenDialog(true);
  };

  const formatToIST = (utcString) => {
    if (!utcString) return "-";
    const date = new Date(utcString);
    const istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
    return istDate.toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
      hour12: true
    });
  };
const LAB_FIELDS = [
    "Nitrogen (Lab Value)",
    "Phosphorus (Lab Value)",
    "Potassium (Lab Value)",
    "Moisture (Lab Value)",
    "Temperature (Lab Value)",
    "Conductivity (Lab Value)",
    "pH (Lab Value)",
    "Lab Analyst Name",
    "Signature",
    "Remarks (Lab)",
    "Other Soil Nutrients"
  ];

    useEffect(() => {
    const fetchData = async () => {
      try {
        let response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/v1/soil_test/generate-sas-token?container=soil-test-agrim`
        );

        if (response.data.status === "success") {
          setToken(response.data.sas_token);
        }
      } catch (err) {
        console.log(err);
      }
    };

    fetchData();
  }, []);

const handleDownloadPDF = () => {
  const input = document.getElementById("pdf-content");
  html2canvas(input, { scale: 2, useCORS: true }).then(canvas => {
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    // Add first page
    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    // Page number on each page
    const pageCount = pdf.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.text(`Page ${i} of ${pageCount}`, pdfWidth - 30, pdfHeight - 10);
    }

    // Local system time for filename (do NOT add 5:30 offset)
    const now = new Date();
    const timestamp = now
      .toLocaleString("en-GB")
      .replace(/[/,:\s]/g, "-");

    const fileName = `soil-test-${selectedRow.id}-${timestamp}.pdf`;
    pdf.save(fileName);
  });
};


  const FIELD_LABELS = {
    id: "Test ID",
    test_start_time: "Test Start Time",
    soil_type: "Soil Type",
    depth_cm: "Depth (inch)",
    latitude: "Latitude",
    longitude: "Longitude",
    image_url: "Image URL",
    probe_placed: "Probe Placed",
    placement_validated: "Placement Validated",
    test_confirmed: "Test Confirmed",
    timer_started_at: "Timer Started At",
    timer_completed_at: "Timer Completed At",
    n: "Nitrogen (mg/kg)",
    p: "Phosphorus (mg/kg)",
    k: "Potassium (mg/kg)",
    moisture_percent: "Moisture (%RH)",
    temperature_c: "Temperature (°C)",
    electrical_conductivity: "Conductivity (µs/cm)",
    ph: "pH",
    remarks: "Remarks",
    test_status: "Test Status"
  };

  const columns = [
    {
      field: "ids",
      headerName: "S.No",
      renderHeader: () => (
        <strong style={{ color: "rgb(28, 70, 146)" }}>{"SL. No"}</strong>
      ),
      headerAlign: "center",
      align: "center",
      renderCell: params => (
        <span style={{ pointerEvents: "none", userSelect: "none" }}>
          {params.api.getAllRowIds().indexOf(params.id) + 1}
        </span>
      )
    },
    {
      field: "view",
      headerName: "Details",
      width: 130,
      renderCell: (params) => (
        <Button variant="outlined" size="small" onClick={() => handleViewDetails(params.row)}>
          View Details
        </Button>
      )
    },
    { field: "id", headerName: "Test ID", width: 70 },
    {
      field: "test_start_time",
      headerName: "Test Start Time",
      width: 200,
      valueFormatter: (params) => formatToIST(params.value)
    },
    { field: "soil_type", headerName: "Soil Type", width: 120 },
    { field: "depth_cm", headerName: "Depth (inch)", width: 120 },
    { field: "latitude", headerName: "Latitude", width: 120 },
    { field: "longitude", headerName: "Longitude", width: 120 },
    { field: "test_status", headerName: "Status", width: 130 }
  ];

  const statusOptions = ["running", "started", "complete", "aborted"];

  return (
    <Box sx={{ marginTop: "10vh" }} py={3}>
      <Typography
        sx={{
          color: "rgba(0, 0, 0, 0.80)",
          fontFamily: "Inter, sans-serif",
          fontSize: "1.5rem",
          fontWeight: 600,
          marginBottom: "1.5rem"
        }}
      >
        Recent Soil Tests
      </Typography>

      <Box mb={2} display="flex" gap={4} flexWrap="wrap" alignItems="center">
        <TextField
          label="From Date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={fromDate}
          onChange={e => setFromDate(e.target.value)}
        />
        <TextField
          label="To Date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={toDate}
          onChange={e => setToDate(e.target.value)}
        />
        <TextField
          select
          label="Status"
          value={status}
          onChange={e => setStatus(e.target.value)}
          style={{ minWidth: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          {statusOptions.map(option => (
            <MenuItem key={option} value={option}>
              {option.replace(/_/g, " ")}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={handleFilter}>Apply</Button>
        <Button variant="outlined" onClick={handleReset}>Reset</Button>
      </Box>

      <DataGrid
        getRowId={row => row.id}
        rows={data}
        columns={columns}
        loading={loading}
        density="compact"
        components={{ Toolbar: GridToolbar }}
        pageSize={10}
        rowsPerPageOptions={[10, 25, 50]}
        style={{ maxWidth: "100%", height: "500px" }}
      />

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 0 }} />
        <DialogContent>
          <Box
            id="pdf-content"
            sx={{
              padding: 3,
              backgroundColor: "#fff",
              fontFamily: "Arial, sans-serif",
              border: "1px solid #ddd",
              borderRadius: 2
            }}
          >
            <Box display="flex" justifyContent="space-between" mb={3}>
              <img src="/Agrim.png" alt="AgriM" style={{ height: 50 }} />
              <Typography variant="h5" fontWeight="bold" align="center" flexGrow={1}>
                Soil Testing Report
              </Typography>
              <img src="/coredata.png" alt="CoreData" style={{ height: 45 }} />
            </Box>

            {selectedRow &&
              Object.entries(selectedRow).map(([key, value]) => 
               {
                  console.log(key, value, 287);

                  if(key=="image_url"){
 return(
                <Box
                  key={key}
                  display="flex"
                  justifyContent="space-between"
                  mb={1}
                  sx={{
                    borderBottom: "1px dashed #ccc",
                    paddingBottom: "4px"
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>
                    {FIELD_LABELS[key] || key.replace(/_/g, " ")}
                  </Typography>
                   <div style={{width: '14.4375rem', height: '7.8125rem'}}>
                        <img src={`${value}?${token}`} alt="" style={{width: '100%', height: '100%', objectFit: 'contain'}}/>
                   </div>
                </Box>
              )

                  }
                  else{
                     return(
                <Box
                  key={key}
                  display="flex"
                  justifyContent="space-between"
                  mb={1}
                  sx={{
                    borderBottom: "1px dashed #ccc",
                    paddingBottom: "4px"
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>
                    {FIELD_LABELS[key] || key.replace(/_/g, " ")}
                  </Typography>
                  <Typography>
                    {typeof value === "boolean"
                      ? value ? "Yes" : "No"
                      : key.toLowerCase().includes("time")
                      ? formatToIST(value)
                      : String(value)}
                  </Typography>
                </Box>
              )
                  }
                  
               }
              )}

            {/* Additional empty lab fields */}
            <Typography mt={4} fontWeight="bold" fontSize={16}>Lab Test Fields:</Typography>
            {LAB_FIELDS.map((label, index) => (
              <Box key={index} display="flex" justifyContent="space-between" mb={1} sx={{
                borderBottom: "1px dashed #ccc",
                paddingBottom: "4px"
              }}>
                <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
                <Typography>__________________</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDownloadPDF} variant="contained" color="success">
            Download PDF
          </Button>
          <Button onClick={() => setOpenDialog(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SoilTestTable;
